import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import dayjs from "dayjs";
import { useServiceCenters } from "../../hooks/react-query/useServiceCenters";
import {
  formatLocationWithSicCodes,
  createSicCodeMap,
  getSicCodeFromId,
} from "./equipmentDropdown.utils";
import type {
  EquipmentBucket,
  EquipmentOptionDisplay,
  EquipmentDropdownProps,
} from "./EquipmentDropdown.types";
import { getEquipmentGroupLabels } from "./EquipmentDropdown.types";
import { AssignmentTypeIcon } from "./AssignmentTypeIcon";
import { LoadSummaryDisplay } from "./LoadSummaryDisplay";
import PlannedShipmentStatusTag from "./StatusTag/PlannedShipmentStatusTag";

export const EquipmentDropdown = React.memo(
  ({
    value,
    onChange,
    options,
    label = "Select Equipment",
    disabled = false,
    getOptionDisabled,
    disableClearable = false,
    error = false,
    helperText,
    equipmentTypeName,

    inputValue,
    onInputChange,
    loading,
    showSicCode = true,
  }: EquipmentDropdownProps) => {
    const serviceCentersQuery = useServiceCenters("All");
    const theme = useTheme();
    const sicCodeMap = useMemo(
      () => createSicCodeMap(serviceCentersQuery.data ?? []),
      [serviceCentersQuery.data]
    );

    const transformedOptions = useMemo(() => {
      const bucketSortOrder: Record<EquipmentBucket, number> = {
        loadAndHold: 0,
        available: 1,
        assigned: 2,
      };
      return options
        .map((option) => ({
          ...option,
          locationDisplay: formatLocationWithSicCodes(option.rawData, (sicId) =>
            getSicCodeFromId(sicId, sicCodeMap)
          ),
        }))
        .sort((a, b) => bucketSortOrder[a.bucket] - bucketSortOrder[b.bucket]);
    }, [options, sicCodeMap]);

    const groupLabels = getEquipmentGroupLabels(equipmentTypeName);

    const groupBy = (option: EquipmentOptionDisplay) => {
      if (option.bucket === "loadAndHold") return groupLabels.loadAndHold;
      return option.isAvailable
        ? groupLabels.available
        : groupLabels.unavailable;
    };

    const getOptionLabel = (option: EquipmentOptionDisplay) => option.refNumber;

    const isOptionEqualToValue = (
      option: EquipmentOptionDisplay,
      value: EquipmentOptionDisplay
    ) => option.id === value.id;

    return (
      <Autocomplete
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        options={transformedOptions}
        groupBy={groupBy}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        disabled={disabled}
        getOptionDisabled={(option) => getOptionDisabled?.(option) ?? false}
        disableClearable={disableClearable}
        inputValue={inputValue}
        onInputChange={
          onInputChange
            ? (_, newValue, reason) => {
                if (reason !== "reset") onInputChange(newValue);
              }
            : undefined
        }
        loading={loading}
        clearOnBlur={onInputChange === undefined}
        sx={{
          "& .MuiAutocomplete-option.Mui-disabled": {
            opacity: 1,
          },
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{
              "&.MuiAutocomplete-option": {
                padding:
                  option.bucket === "assigned" ||
                  option.bucket === "loadAndHold"
                    ? "4px 8px"
                    : undefined,
              },
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              width="100%"
              gap={0.5}
              sx={
                option.bucket !== "available"
                  ? {
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: "8px",
                      padding: "12px 16px",
                    }
                  : undefined
              }
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color:
                        option.bucket === "available"
                          ? theme.palette.text.primary
                          : theme.palette.grey[500],
                      fontSize: 16,
                    }}
                  >
                    {option.refNumber}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  {option.bucket !== "available" &&
                  option.rawData.assignedTo?.linehaulInfo &&
                  (option.rawData.assignedTo.linehaulInfo.originSicId ||
                    option.rawData.assignedTo.linehaulInfo.destinationSicId) ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        sx={{
                          color: theme.palette.grey[500],
                        }}
                      >
                        {option.rawData.assignedTo.linehaulInfo.originSicId
                          ? getSicCodeFromId(
                              option.rawData.assignedTo.linehaulInfo
                                .originSicId,
                              sicCodeMap
                            )
                          : null}
                      </Typography>
                      <ArrowForward
                        sx={{
                          fontSize: "16px",
                          color: theme.palette.grey[500],
                        }}
                      />
                      <Typography
                        sx={{
                          color: theme.palette.grey[500],
                        }}
                      >
                        {option.rawData.assignedTo.linehaulInfo.destinationSicId
                          ? getSicCodeFromId(
                              option.rawData.assignedTo.linehaulInfo
                                .destinationSicId,
                              sicCodeMap
                            )
                          : null}
                      </Typography>
                    </Box>
                  ) : showSicCode ? (
                    <Typography
                      sx={{
                        color: theme.palette.grey[500],
                      }}
                    >
                      {getSicCodeFromId(option.rawData.homeSicId, sicCodeMap)}
                    </Typography>
                  ) : null}
                  <PlannedShipmentStatusTag
                    label={option.statusName}
                    isPlannedForToday={false}
                    selectedDate={dayjs().toISOString()}
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {option.rawData.assignedTo && (
                    <AssignmentTypeIcon
                      assignmentType={option.rawData.assignedTo.type}
                    />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "143%",
                      letterSpacing: "0.17px",
                      color: theme.palette.grey[500],
                    }}
                  >
                    {option.rawData.assignedTo?.name}
                  </Typography>
                </Box>
                {option.bucket !== "available" &&
                  option.rawData.equipmentType.name !== "Tractor" && (
                    <LoadSummaryDisplay
                      loadSummary={option.rawData.loadSummary}
                    />
                  )}
              </Box>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
          />
        )}
      />
    );
  }
);

EquipmentDropdown.displayName = "EquipmentDropdown";

// Re-export types for convenience
export type { EquipmentOptionDisplay, EquipmentDropdownProps };
